import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface UiState {
  sidebarOpen: boolean;
  sidebarWidth: number;
  snackbar: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
    autoHideDuration: number;
  };
  rejectionModal: {
    open: boolean;
    selectedEntity: any;
    type: 'screen' | 'campaign' | null;
  };
  imagePreviewModal: {
    open: boolean;
    imageUrl: string | null;
  };
  goBackModal: {
    open: boolean;
  };
  consolidatedReportModal: {
    open: boolean;
    deviceIds: string[];
    campaignIds: string[];
    from: string | null;
    to: string | null;
  };
}

const initialState: UiState = {
  sidebarOpen: false, // Will be set based on screen size
  sidebarWidth: 220,
  snackbar: {
    open: false,
    message: '',
    severity: 'info',
    autoHideDuration: 2000,
  },
  rejectionModal: {
    open: false,
    selectedEntity: null,
    type: null,
  },
  imagePreviewModal: {
    open: false,
    imageUrl: null,
  },
  goBackModal: {
    open: false,
  },
  consolidatedReportModal: {
    open: false,
    deviceIds: [],
    campaignIds: [],
    from: null,
    to: null,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebar: state => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarWidth: (state, action: PayloadAction<number>) => {
      state.sidebarWidth = action.payload;
    },
    closeSidebar: state => {
      state.sidebarOpen = false;
    },
    openSidebar: state => {
      state.sidebarOpen = true;
    },
    initializeSidebarState: (
      state,
      action: PayloadAction<{ isMobile: boolean }>
    ) => {
      // Initialize sidebar state based on screen size
      state.sidebarOpen = !action.payload.isMobile;
    },
    handleSnackbar: (
      state,
      action: PayloadAction<{
        open: boolean;
        message?: string;
        severity?: 'success' | 'error' | 'warning' | 'info';
        autoHideDuration?: number;
      }>
    ) => {
      state.snackbar.open = action.payload.open;
      if (action.payload.message !== undefined) {
        state.snackbar.message = action.payload.message;
      }
      if (action.payload.severity !== undefined) {
        state.snackbar.severity = action.payload.severity;
      }
      if (action.payload.autoHideDuration !== undefined) {
        state.snackbar.autoHideDuration = action.payload.autoHideDuration;
      }
    },
    setRejectionModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        selectedEntity?: any;
        type?: 'screen' | 'campaign' | null;
      }>
    ) => {
      state.rejectionModal.open = action.payload.open;
      if (action.payload.selectedEntity !== undefined) {
        state.rejectionModal.selectedEntity = action.payload.selectedEntity;
      }
      if (action.payload.type !== undefined) {
        state.rejectionModal.type = action.payload.type;
      }
    },
    setImagePreviewModal: (state, action: PayloadAction<string | null>) => {
      state.imagePreviewModal.open = action.payload !== null;
      state.imagePreviewModal.imageUrl = action.payload;
    },
    setGoBackModal: (state, action: PayloadAction<{ open: boolean }>) => {
      state.goBackModal.open = action.payload.open;
    },
    setConsolidatedReportModal: (
      state,
      action: PayloadAction<{
        open: boolean;
        deviceIds?: string[];
        campaignIds?: string[];
        from?: string | null;
        to?: string | null;
      }>
    ) => {
      state.consolidatedReportModal.open = action.payload.open;
      if (action.payload.deviceIds !== undefined)
        state.consolidatedReportModal.deviceIds = action.payload.deviceIds;
      if (action.payload.campaignIds !== undefined)
        state.consolidatedReportModal.campaignIds = action.payload.campaignIds;
      if (action.payload.from !== undefined)
        state.consolidatedReportModal.from = action.payload.from;
      if (action.payload.to !== undefined)
        state.consolidatedReportModal.to = action.payload.to;
    },
  },
});

export const {
  setSidebarOpen,
  toggleSidebar,
  setSidebarWidth,
  closeSidebar,
  openSidebar,
  initializeSidebarState,
  handleSnackbar,
  setRejectionModal,
  setImagePreviewModal,
  setGoBackModal,
  setConsolidatedReportModal,
} = uiSlice.actions;

export default uiSlice.reducer;
